import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Ban, Search, Send } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ImageField } from "@/components/ImageField";
import { Switch } from "@/components/ui/switch";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n) || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—");

const STATUS_STYLE = {
  attiva: "text-emerald-700 border-emerald-700/40",
  parzialmente_utilizzata: "text-gold border-gold/50",
  utilizzata: "text-taupe border-hair",
  scaduta: "text-red-700 border-red-700/40",
  annullata: "text-red-700 border-red-700/40",
};
const STATUS_LABEL = {
  attiva: "Attiva", parzialmente_utilizzata: "Parzialmente utilizzata", utilizzata: "Utilizzata",
  scaduta: "Scaduta", annullata: "Annullata",
};

const btn = "min-h-[40px] inline-flex items-center gap-1.5 text-[11px] tracking-[0.12em] uppercase px-3 py-2 border transition-colors disabled:opacity-50";

export function GiftCardsPanel({ settings, onSaveSettings }) {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [amounts, setAmounts] = useState("50, 100, 150, 250, 500");
  const [gcImage, setGcImage] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/gift-cards${search.trim() ? `?q=${encodeURIComponent(search.trim())}` : ""}`);
      setItems(data);
    } catch { toast.error("Errore caricamento Gift Card"); }
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  useEffect(() => {
    setAmounts(((settings?.giftcard_amounts) || [50, 100, 150, 250, 500]).join(", "));
    setGcImage(settings?.giftcard_image || "");
  }, [settings]);

  const saveConfig = () => {
    const parsed = amounts.split(",").map((x) => parseFloat(x.trim())).filter((x) => !isNaN(x) && x > 0);
    if (!parsed.length) { toast.error("Inserisci almeno un importo valido"); return; }
    onSaveSettings({ giftcard_amounts: parsed, giftcard_image: gcImage });
  };

  const openDetail = async (code) => {
    try { const { data } = await api.get(`/admin/gift-cards/${code}`); setDetail(data); }
    catch { toast.error("Gift Card non trovata"); }
  };

  const cancel = async () => {
    if (!window.confirm(`Annullare la Gift Card ${detail.code}?\n\nIl credito non sarà più utilizzabile. Il pagamento dell'acquisto NON viene rimborsato automaticamente.`)) return;
    try { await api.post(`/admin/gift-cards/${detail.code}/cancel`); toast.success("Gift Card annullata"); setDetail(null); load(); }
    catch { toast.error("Operazione non riuscita"); }
  };

  const resend = async () => {
    try { await api.post(`/admin/gift-cards/${detail.code}/resend`); toast.success(`Gift Card re-inviata a ${detail.recipient_email}`); }
    catch { toast.error("Invio non riuscito"); }
  };

  if (detail) {
    return (
      <div className="space-y-6" data-testid="giftcard-detail">
        <button onClick={() => { setDetail(null); load(); }} className={`${btn} border-hair hover:border-ink`} data-testid="giftcard-back"><ArrowLeft size={14} /> Tutte le Gift Card</button>
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <div className="bg-white border border-hair p-5 md:p-6">
            <div className="eyebrow mb-3">Gift Card</div>
            <p className="font-mono text-xl tracking-wider" data-testid="giftcard-detail-code">{detail.code}</p>
            <span className={`inline-block mt-3 text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${STATUS_STYLE[detail.status]}`} data-testid="giftcard-detail-status">{STATUS_LABEL[detail.status]}</span>
            <dl className="mt-5 space-y-2 text-sm">
              {[
                ["Valore iniziale", eur(detail.initial_value)],
                ["Saldo residuo", eur(detail.balance)],
                ["Mittente", detail.sender_name || "—"],
                ["Destinatario", detail.recipient_name || "—"],
                ["Email destinatario", detail.recipient_email],
                ["Acquistata il", fmtDate(detail.created_at)],
                ["Scade il", fmtDate(detail.expires_at)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-hair/60 pb-2">
                  <dt className="text-taupe">{k}</dt><dd className="text-right font-medium break-all">{v}</dd>
                </div>
              ))}
              {detail.message && <div className="pt-2"><dt className="text-taupe text-xs uppercase tracking-[0.12em]">Messaggio</dt><dd className="italic mt-1">“{detail.message}”</dd></div>}
            </dl>
            <div className="flex gap-2 mt-5 flex-wrap">
              <button onClick={resend} className={`${btn} border-ink hover:bg-ink hover:text-bone`} data-testid="giftcard-resend"><Send size={13} /> Reinvia email</button>
              {detail.status !== "annullata" && <button onClick={cancel} className={`${btn} border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="giftcard-cancel"><Ban size={13} /> Annulla</button>}
            </div>
          </div>
          <div className="bg-white border border-hair p-5 md:p-6" data-testid="giftcard-uses">
            <div className="eyebrow mb-3">Storico utilizzi</div>
            {(detail.uses || []).length === 0 && <p className="text-sm text-taupe">Nessun utilizzo ancora registrato.</p>}
            {(detail.uses || []).slice().reverse().map((u, i) => (
              <div key={i} className="text-xs py-2.5 border-b border-hair/60 last:border-0">
                <div className="flex justify-between">
                  <span className="text-taupe">{fmtDate(u.at)}</span>
                  <span className="font-medium">− {eur(u.amount)}</span>
                </div>
                <div className="flex justify-between text-taupe mt-0.5">
                  <span className="font-mono truncate">{u.ref}</span>
                  <span>{eur(u.balance_before)} → {eur(u.balance_after)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="giftcards-panel">
      {/* Configurazione */}
      <div className="bg-white border border-hair p-5 md:p-6">
        <div className="eyebrow mb-3">Configurazione Gift Card</div>
        <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[48px] max-w-xl mb-5 pb-5 border-b border-hair">
          <Switch
            checked={settings?.giftcard_enabled !== false}
            onCheckedChange={(v) => onSaveSettings({ giftcard_enabled: v })}
            data-testid="giftcard-enabled"
          />
          <span>
            <span className="block font-medium">Attiva vendita Gift Card {settings?.giftcard_enabled !== false ? <span className="text-emerald-700">— ATTIVA</span> : <span className="text-red-700">— DISATTIVA</span>}</span>
            <span className="block text-xs text-taupe mt-0.5">Se disattiva: la pagina /gift-card non consente nuovi acquisti e l'accesso diretto è bloccato. Le Gift Card già emesse restano valide e utilizzabili.</span>
          </span>
        </label>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <p className="text-[10px] tracking-[0.14em] uppercase text-taupe mb-2">Importi acquistabili (€, separati da virgola)</p>
            <Input value={amounts} onChange={(e) => setAmounts(e.target.value)} className="rounded-none" data-testid="giftcard-amounts" />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.14em] uppercase text-taupe mb-2">Immagine Gift Card (pagina pubblica + email)</p>
            <ImageField value={gcImage} onChange={setGcImage} testId="giftcard-image-field" />
          </div>
        </div>
        <button onClick={saveConfig} className={`${btn} bg-ink text-bone border-ink hover:bg-gold hover:text-ink mt-4`} data-testid="giftcard-save-config">Salva configurazione</button>
      </div>

      {/* Ricerca + lista */}
      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-taupe" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per codice, nome o email…" className="rounded-none pl-9" data-testid="giftcard-search" />
      </div>
      {!items ? <p className="text-taupe text-sm">Caricamento…</p> : (
        <div className="bg-white border border-hair overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="border-b border-hair text-left">
              <tr>
                {["Codice", "Valore iniziale", "Saldo residuo", "Mittente", "Destinatario", "Email", "Acquisto", "Scadenza", "Stato"].map((h) => (
                  <th key={h} className="p-4 font-normal eyebrow">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="9" className="p-8 text-center text-taupe">Nessuna Gift Card emessa.</td></tr>}
              {items.map((g) => (
                <tr key={g.id} onClick={() => openDetail(g.code)} className="border-b border-hair last:border-0 cursor-pointer hover:bg-bone/60" data-testid={`giftcard-row-${g.code}`}>
                  <td className="p-4 font-mono text-xs">{g.code}</td>
                  <td className="p-4">{eur(g.initial_value)}</td>
                  <td className="p-4 font-medium">{eur(g.balance)}</td>
                  <td className="p-4">{g.sender_name || "—"}</td>
                  <td className="p-4">{g.recipient_name || "—"}</td>
                  <td className="p-4 text-xs">{g.recipient_email}</td>
                  <td className="p-4 text-xs text-taupe">{fmtDate(g.created_at)}</td>
                  <td className="p-4 text-xs text-taupe">{fmtDate(g.expires_at)}</td>
                  <td className="p-4"><span className={`text-[10px] tracking-[0.12em] uppercase border px-2 py-1 ${STATUS_STYLE[g.status]}`}>{STATUS_LABEL[g.status]}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
